import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  ArrowRight,
  Search,
  User,
  Sparkles,
  Calculator,
  ShieldCheck,
} from "lucide-react";
import { BLOG_CATEGORIES, blogPosts, featuredPost } from "@/lib/blogData";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const Blog = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] =
    useState<(typeof BLOG_CATEGORIES)[number]>("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blogPosts
      .filter((p) => !p.featured)
      .filter((p) =>
        activeCategory === "All" ? true : p.category === activeCategory
      )
      .filter((p) =>
        !q
          ? true
          : [p.title, p.excerpt, p.category, ...p.tags]
              .join(" ")
              .toLowerCase()
              .includes(q)
      );
  }, [query, activeCategory]);

  const blogListJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Neil AI Insights",
    url: "https://www.neilai.ai/blog",
    description:
      "Real-world guidance on AI automation, PUDU Robotics, cleaning robots, service robots, voice AI, and ROI.",
    blogPost: blogPosts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `https://www.neilai.ai/blog/${p.slug}`,
      datePublished: p.date,
      author: { "@type": "Organization", name: "Neil AI Global Solutions" },
      image: p.image,
    })),
  };

  return (
    <>
      <SEO
        title="Neil AI Blog | AI Automation, PUDU Robotics & Business Technology Insights"
        description="Explore Neil AI insights on AI automation, PUDU Robotics, cleaning robots, service robots, voice AI, ROI, and business transformation for modern companies."
        keywords="Neil AI blog, AI automation, PUDU robotics, cleaning robots, service robots, voice AI, ROI, business automation"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogListJsonLd) }}
      />

      <div className="min-h-screen flex flex-col bg-[#05070A]">
        <Navigation />

        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#071A33] via-[#05070A] to-[#05070A]" />
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 50% at 50% 0%, hsl(206 100% 50% / 0.25), transparent 70%)",
            }}
          />
          <div className="relative container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <Badge className="mb-5 bg-[#00A3FF]/10 text-[#00A3FF] border border-[#00A3FF]/30 hover:bg-[#00A3FF]/10">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Neil AI Insights
              </Badge>
              <h1 className="font-bold text-4xl sm:text-5xl md:text-6xl tracking-tight text-white leading-[1.05]">
                Practical insight on{" "}
                <span className="bg-gradient-to-r from-[#00A3FF] to-[#7DD3FC] bg-clip-text text-transparent">
                  AI &amp; robotics
                </span>{" "}
                for real businesses.
              </h1>
              <p className="mt-5 text-base md:text-lg text-slate-300/90 max-w-2xl mx-auto">
                Real-world guidance on AI automation, PUDU Robotics, business
                operations, cleaning automation, service robots, and ROI — written
                for owners and operators.
              </p>

              {/* Search */}
              <div className="mt-8 max-w-xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search articles, robots, industries, ROI, automation..."
                    className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-400 focus-visible:ring-[#00A3FF]/60"
                    aria-label="Search Neil AI blog"
                  />
                </div>
              </div>

              {/* CTA buttons */}
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#00A3FF] hover:bg-[#0057FF] text-white shadow-[0_0_30px_-8px_rgba(0,163,255,0.7)]"
                >
                  <Link to="/book-demo">Book a Free Demo</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to="/roi-calculator">
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate ROI
                  </Link>
                </Button>
              </div>
            </div>

            {/* Category pills */}
            <div className="mt-10 flex flex-wrap justify-center gap-2">
              {BLOG_CATEGORIES.map((cat) => {
                const active = cat === activeCategory;
                const slug = cat === "All" ? null : cat.toLowerCase().replace(/\s+/g, "-");
                const cls =
                  "px-4 py-1.5 text-xs sm:text-sm rounded-full border transition-all " +
                  (active
                    ? "bg-[#00A3FF] border-[#00A3FF] text-white shadow-[0_0_18px_-4px_rgba(0,163,255,0.8)]"
                    : "bg-white/5 border-white/10 text-slate-300 hover:border-[#00A3FF]/50 hover:text-white");
                if (!slug) {
                  return <button key={cat} onClick={() => setActiveCategory("All")} className={cls}>{cat}</button>;
                }
                return (
                  <Link key={cat} to={`/blog/category/${slug}`} className={cls}>{cat}</Link>
                );
              })}
            </div>
          </div>
        </section>

        <main className="flex-grow container mx-auto px-4 md:px-6 pb-24">
          {/* FEATURED */}
          {featuredPost && (
            <section className="mb-16 md:mb-20" aria-labelledby="featured-heading">
              <h2 id="featured-heading" className="sr-only">
                Featured article
              </h2>
              <Link to={`/blog/${featuredPost.slug}`} className="block group">
                <Card className="overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-[#00A3FF]/50 transition-all duration-300 shadow-[0_0_60px_-30px_rgba(0,163,255,0.4)]">
                  <div className="grid md:grid-cols-5">
                    <div className="md:col-span-3 relative overflow-hidden bg-[#05070A]">
                      <img
                        src={featuredPost.image}
                        alt={featuredPost.imageAlt}
                        loading="eager"
                        className="w-full h-64 md:h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4 flex gap-2">
                        <Badge className="bg-[#D6A84F] text-black hover:bg-[#D6A84F]">
                          Featured
                        </Badge>
                        <Badge className="bg-black/60 text-white border border-white/20">
                          {featuredPost.category}
                        </Badge>
                      </div>
                    </div>
                    <div className="md:col-span-2 p-6 md:p-10 flex flex-col justify-center">
                      <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                        {featuredPost.title}
                      </h3>
                      <p className="mt-4 text-slate-300/90 text-sm md:text-base">
                        {featuredPost.excerpt}
                      </p>
                      <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" />
                          {featuredPost.author}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(featuredPost.date)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {featuredPost.readTime}
                        </span>
                      </div>
                      <Button className="mt-6 w-fit bg-[#00A3FF] hover:bg-[#0057FF] text-white">
                        Read Featured Guide
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </Link>
            </section>
          )}

          {/* ARTICLE GRID */}
          <section aria-labelledby="all-articles-heading">
            <div className="flex items-end justify-between mb-6 md:mb-8">
              <div>
                <h2
                  id="all-articles-heading"
                  className="text-2xl md:text-3xl font-bold text-white"
                >
                  {activeCategory === "All" ? "Latest Articles" : activeCategory}
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  {filtered.length} {filtered.length === 1 ? "article" : "articles"}
                </p>
              </div>
            </div>

            {filtered.length === 0 ? (
              <Card className="p-10 text-center bg-white/[0.03] border-white/10 text-slate-300">
                No articles match that search yet. Try a different category or
                keyword.
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filtered.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A3FF] rounded-2xl"
                  >
                    <Card className="h-full flex flex-col overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-[#00A3FF]/50 hover:shadow-[0_0_40px_-12px_rgba(0,163,255,0.5)] transition-all duration-300">
                      <div className="relative h-48 overflow-hidden bg-[#05070A]">
                        <img
                          src={post.image}
                          alt={post.imageAlt}
                          loading="lazy"
                          width={800}
                          height={500}
                          className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                        />
                        <Badge className="absolute top-3 left-3 bg-black/70 text-[#00A3FF] border border-[#00A3FF]/40">
                          {post.category}
                        </Badge>
                      </div>
                      <div className="p-5 md:p-6 flex flex-col flex-1">
                        <h3 className="text-lg md:text-xl font-bold text-white leading-snug line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="mt-3 text-sm text-slate-300/90 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                        <div className="mt-auto pt-5">
                          <span className="inline-flex items-center text-sm font-semibold text-[#00A3FF] group-hover:text-white transition-colors">
                            Read Article
                            <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* NEWSLETTER */}
          <section className="mt-20 md:mt-28" aria-labelledby="newsletter-heading">
            <Card className="relative overflow-hidden p-6 sm:p-10 md:p-14 bg-gradient-to-br from-[#071A33] via-[#05070A] to-[#05070A] border border-white/10">
              <div
                className="absolute inset-0 opacity-50 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 80% at 100% 0%, hsl(206 100% 50% / 0.20), transparent 70%)",
                }}
              />
              <div className="relative grid md:grid-cols-5 gap-8 items-center">
                <div className="md:col-span-3">
                  <h2
                    id="newsletter-heading"
                    className="text-2xl md:text-3xl font-bold text-white"
                  >
                    Get AI &amp; Robotics Insights Before Your Competitors Do
                  </h2>
                  <p className="mt-3 text-slate-300/90">
                    Join the Neil AI newsletter for practical automation tips,
                    robot updates, ROI guides, and business growth strategies.
                  </p>
                </div>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="md:col-span-2 space-y-3"
                >
                  <Input
                    placeholder="Your name"
                    className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-400"
                  />
                  <Input
                    type="email"
                    placeholder="Email address"
                    className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-400"
                  />
                  <Input
                    placeholder="Business type (e.g. Hotel, Restaurant)"
                    className="h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-400"
                  />
                  <Button
                    type="submit"
                    className="w-full h-11 bg-[#00A3FF] hover:bg-[#0057FF] text-white"
                  >
                    Subscribe
                  </Button>
                </form>
              </div>
            </Card>
          </section>

          {/* ROI CTA */}
          <section className="mt-12 md:mt-16" aria-labelledby="roi-cta-heading">
            <Card className="relative overflow-hidden p-6 sm:p-10 md:p-14 bg-[#05070A] border border-[#00A3FF]/30">
              <div
                className="absolute inset-0 opacity-60 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(50% 80% at 0% 100%, hsl(206 100% 50% / 0.25), transparent 70%)",
                }}
              />
              <div className="relative max-w-3xl">
                <Badge className="mb-4 bg-[#D6A84F]/15 text-[#D6A84F] border border-[#D6A84F]/30 hover:bg-[#D6A84F]/15">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                  Free Automation Review
                </Badge>
                <h2
                  id="roi-cta-heading"
                  className="text-2xl md:text-4xl font-bold text-white leading-tight"
                >
                  Want to know what automation could save your business?
                </h2>
                <p className="mt-4 text-slate-300/90 text-base md:text-lg">
                  Neil AI can help review your current workflow, staffing
                  pressure, cleaning needs, and customer service gaps to
                  determine where AI and robotics can make the biggest impact.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Button
                    asChild
                    size="lg"
                    className="bg-[#00A3FF] hover:bg-[#0057FF] text-white shadow-[0_0_30px_-8px_rgba(0,163,255,0.7)]"
                  >
                    <Link to="/book-demo">Book Free Automation Review</Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 hover:text-white"
                  >
                    <Link to="/robots">View Robot Solutions</Link>
                  </Button>
                </div>
              </div>
            </Card>
          </section>

          {/* Mobile bottom padding so floating chat doesn't cover content */}
          <div className="h-24 md:h-0" aria-hidden />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
